import React, { useState, useEffect, useRef } from "react";
import FormCard from "./FormCard";
import { formatDate } from "../../utils/utils.js";
import Select from "react-select";
import { customDropdownStyles } from "../../data/data";
import { useApiServiceInference } from "../../services/apiServiceInference.js";
import { formValidationSchemaModels } from "../../schema/formValidationSchema.js";
import ActiveModelsTable from "../Common/ActiveModelsTable.jsx";
import AnomaliesTable from "../Common/AnomaliesTable.jsx";
import InferenceTimeseriesChart from "../../charts/InferenceTimeseriesChart.jsx";
import toast from "react-hot-toast";
import Loading from "../Common/Loading.jsx";
import ConfirmationPopup from "../Common/ConfirmationPopup.jsx";

const InferenceForm = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const datasourceParam = queryParams.get("datasource");
  const dgsTagsParams = queryParams.get("dgsTags");
  const modelStartDate = queryParams.get("startDate");
  const modelEndDate = queryParams.get("endDate");

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [activeModels, setActiveModels] = useState([]);
  const [inferenceData, setInferenceData] = useState({});
  const [, setTotalPages] = useState(1);
  // State for selected anomaly (row)
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // Calculate default dates
  const today = new Date();

  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() - 1); // 1 day before today

  const startDate = new Date(today);
  startDate.setMonth(startDate.getMonth() - 6); // 6 months before today

  // Add state for start and end date
  const [startDateState, setStartDateState] = useState(
    modelStartDate ? modelStartDate : formatDate(startDate)
  );
  const [endDateState, setEndDateState] = useState(
    modelEndDate ? modelEndDate : formatDate(endDate)
  );

  const [dropdownOptions, setDropdownOptions] = useState({
    assemblyOptions: [],
    dryGasSealTagsOptions: [],
    tagsOptions: [],
  });

  const [selectedValues, setSelectedValues] = useState({
    selectedAssembly: null,
    selectedDryGasSealTags: null,
    selectedTags: [],
  });

  const mapAssembly = (item) => ({
    label: item.DRIVER + " " + item.PROJECTNAME,
    value: item.DRIVER + " " + item.PROJECTNAME,
    datasource: item.DATASOURCE,
  });
  const mapDryGasSealTags = (item) => {
    const depTagAliasArray = item.TAG_ALIAS_LIST.replace(/[[\]']/g, "").split(
      ", "
    );
    const formattedString = depTagAliasArray.map((tag) => `${tag}`).join(",");
    return {
      label: formattedString,
      value: formattedString,
      tagName: item.TAG_NAME,
      modelId: item.MODEL_ID,
      tagAliasList: item.TAG_ALIAS_LIST,
    };
  };
  const mapTags = (tagAlias) => ({
    label: tagAlias,
    value: tagAlias,
  });

  const {
    getAssemblies,
    getDryGasSealTagsSelection,
    getTagsPaginationInfo,
    getTagsCombination,
    getTags,
    getActiveModels,
  } = useApiServiceInference();

  const normalizeTagsResponse = (data) => {
    if (Array.isArray(data)) {
      const tag_aliases = data.map((tag) => tag.tag_alias);
      const timestamps = data[0]?.timestamps || [];
      const values = {};
      const anomalies = {};

      tag_aliases.forEach((alias, idx) => {
        values[alias] = data[idx].values || [];
        anomalies[alias] = data[idx].anomalies || [];
      });

      return { tag_aliases, timestamps, values, anomalies };
    }

    return data ?? {};
  };

  const mergeInferenceData = (currentData, nextData) => {
    const safeCurrent = currentData ?? {};
    const safeNext = nextData ?? {};
    const currentAliases = safeCurrent.tag_aliases ?? [];
    const nextAliases = safeNext.tag_aliases ?? [];
    const mergedAliases = Array.from(new Set([...currentAliases, ...nextAliases]));

    const mergedValues = { ...(safeCurrent.values ?? {}) };
    const mergedAnomalies = { ...(safeCurrent.anomalies ?? {}) };

    nextAliases.forEach((alias) => {
      const previousValues = mergedValues[alias] ?? [];
      const previousAnomalies = mergedAnomalies[alias] ?? [];
      const nextValues = safeNext.values?.[alias] ?? [];
      const nextAnomalies = safeNext.anomalies?.[alias] ?? [];

      mergedValues[alias] = [...previousValues, ...nextValues];
      mergedAnomalies[alias] = [...previousAnomalies, ...nextAnomalies];
    });

    return {
      ...safeCurrent,
      ...safeNext,
      tag_aliases: mergedAliases,
      timestamps: [...(safeCurrent.timestamps ?? []), ...(safeNext.timestamps ?? [])],
      values: mergedValues,
      anomalies: mergedAnomalies,
    };
  };

  const fetchPaginatedInferenceData = async (from, to, modelName) => {
    const paginationInfo = await getTagsPaginationInfo(from, to, modelName);
    const pages = Math.max(1, Number(paginationInfo?.total_pages) || 1);
    setTotalPages(pages);

    let mergedData = {};
    for (let page = 1; page <= pages; page += 1) {
      const pageDataRaw = await getTagsCombination(from, to, modelName, page);
      const pageData = normalizeTagsResponse(pageDataRaw);
      mergedData = mergeInferenceData(mergedData, pageData);

      // Keep chart/table updating while pages are still being fetched.
      setInferenceData({ ...mergedData });
      if (Array.isArray(mergedData.tag_aliases)) {
        setDropdownOptions((prev) => ({
          ...prev,
          tagsOptions: mergedData.tag_aliases.map(mapTags),
        }));
      }
    }

    return mergedData;
  };

  const fetchDropdownData = async (
    dropdownName,
    apiCall,
    mapFunction,
    datasource = null
  ) => {
    try {
      const data = await apiCall(datasource);

      if (dropdownName === "tags" && data && Array.isArray(data.tag_aliases)) {
        const options = data.tag_aliases.map(mapFunction);
        setDropdownOptions((prev) => ({
          ...prev,
          tagsOptions: options,
        }));
        setInferenceData(data ?? {});
      }

      if (Array.isArray(data)) {
        const options = data.map(mapFunction);
        const uniqueOptions = Array.from(
          new Set(options.map((option) => option.value))
        ).map((value) => options.find((option) => option.value === value));
        setDropdownOptions((prev) => ({
          ...prev,
          [`${dropdownName}Options`]: uniqueOptions,
        }));

        // Set default selected values based on URL parameters
        if (dropdownName === "assembly" && datasourceParam && dgsTagsParams) {
          const defaultAssembly = options.find(
            (option) => {
              return String(option.datasource).trim() === String(datasourceParam).trim()
            }
          );
            setSelectedValues((prev) => ({
              ...prev,
              selectedAssembly: defaultAssembly,
            }));
          const dryGasSealTagsData = await fetchDropdownData(
            "dryGasSealTags",
            getDryGasSealTagsSelection,
            mapDryGasSealTags,
            datasourceParam
          );
          if (dgsTagsParams && dryGasSealTagsData) {
            const dryGasSealTagsOptions =
              dryGasSealTagsData.map(mapDryGasSealTags);
            const defaultDryGasSealTag = dryGasSealTagsOptions.find(
              (option) => option.value === dgsTagsParams
            );
            if (defaultDryGasSealTag) {
              setSelectedValues((prev) => ({
                ...prev,
                selectedDryGasSealTags: defaultDryGasSealTag,
              }));
              try {
                const tagAliasList = JSON.parse(
                  defaultDryGasSealTag.tagAliasList.replace(/'/g, '"')
                );
                const fetchedTags = await fetchPaginatedInferenceData(
                  startDateState,
                  endDateState,
                  defaultDryGasSealTag.modelId
                );

                const activeModels = await getActiveModels(
                  tagAliasList,
                  datasourceParam
                );
                setActiveModels(activeModels);
                setInferenceData(fetchedTags ?? {});
              } catch (e) {
                toast.error(
                  "Error fetching tags or active models for selected DGS tag."
                );
                console.error(e);
              }
            }
          }
        }
        return data;
      } else if (data && typeof data === "object") {
        // For inferenceData, set as object directly
        if (dropdownName === "tags") {
          setInferenceData(data ?? {});
        }
        return data;
      }
    } catch (error) {
      toast.error(
        `Error fetching ${dropdownName} data. Please try again later.`
      );
      console.error(
        `Error fetching ${dropdownName} data in Models Screen:`,
        error
      );
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchDropdownData("assembly", getAssemblies, mapAssembly);
      setLoading(false);
    };
    fetchData();
  }, []);

  const validateField = (fieldName, value) => {
    formValidationSchemaModels
      .validateAt(fieldName, { [fieldName]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
      })
      .catch((validationError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: validationError.message,
        }));
      });
  };

  const handleSelectChange = async (name, selectedOption) => {
    if (name === "selectedAssembly") {
      setSelectedValues((prev) => ({
        ...prev,
        selectedAssembly: selectedOption,
        selectedDryGasSealTags: null,
        selectedTags: [],
      }));
    } else if (name === "selectedDryGasSealTags") {
      setSelectedValues((prev) => ({
        ...prev,
        selectedDryGasSealTags: selectedOption,
        selectedTags: [],
      }));
    } else {
      setSelectedValues((prev) => ({ ...prev, [name]: selectedOption }));
    }

    const fieldsToValidate = ["selectedAssembly", "selectedDryGasSealTags"];
    if (fieldsToValidate.includes(name)) {
      validateField(name, selectedOption);
    }

    const dataSourceForDgsTags =
      name === "selectedAssembly"
        ? selectedOption?.datasource
        : selectedValues.selectedAssembly?.datasource;
    if (name === "selectedAssembly" && selectedOption) {
      const datasource = selectedOption.datasource;
      setLoading(true);
      try {
        await fetchDropdownData(
          "dryGasSealTags",
          getDryGasSealTagsSelection,
          mapDryGasSealTags,
          datasource
        );
      } catch (e) {
        toast.error(
          "Error fetching Dry Gas Seal Tags. Please try again later.",
          e
        );
      } finally {
        setLoading(false);
      }
    } else if (name === "selectedDryGasSealTags" && selectedOption) {
      const modelName = selectedOption.modelId;
      const tagAlias = selectedOption.value;
      const tagAliasList = JSON.parse(
        selectedOption.tagAliasList.replace(/'/g, '"')
      );
      setLoading(true);
      setActiveModels([]);
      try {
        const fetchedTags = await fetchPaginatedInferenceData(
          startDateState,
          endDateState,
          modelName
        );
        const activeModels = await getActiveModels(
          tagAliasList,
          dataSourceForDgsTags
        );
        setActiveModels(activeModels);
        setInferenceData(fetchedTags ?? {});
      } catch (e) {
        console.log(e);
        toast.error("Error fetching DGS Tags. Please try again later.");
      } finally {
        setLoading(false);
      }
    } else if (name === "selectedTags" && selectedOption) {
      const modelNameForTags =
        name === "selectedDryGasSealTags"
          ? selectedOption?.modelId
          : selectedValues.selectedDryGasSealTags.modelId;
      const tagAlias = selectedOption.map((option) => option.value);
      setLoading(true);
      try {
        const fetchedTagsRaw = await getTags(
          startDateState,
          endDateState,
          modelNameForTags,
          tagAlias.length === 0
            ? selectedValues.selectedDryGasSealTags.value
            : tagAlias
        );
        const fetchedTags = normalizeTagsResponse(fetchedTagsRaw);
        setInferenceData(fetchedTags ?? {});
      } catch (e) {
        console.log(e);
        toast.error("Error fetching Single Tags. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!selectedValues.selectedDryGasSealTags) return;
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        let fetchedTags = {};
        if (
          selectedValues.selectedTags &&
          selectedValues.selectedTags.length > 0
        ) {
          const modelNameForTags =
            selectedValues.selectedDryGasSealTags.modelId;
          const tagAlias = selectedValues.selectedTags.map(
            (option) => option.value
          );
          fetchedTags = await getTags(
            startDateState,
            endDateState,
            modelNameForTags,
            tagAlias.length === 0
              ? selectedValues.selectedDryGasSealTags.value
              : tagAlias
          );
          fetchedTags = normalizeTagsResponse(fetchedTags);
        } else {
          const modelName = selectedValues.selectedDryGasSealTags.modelId;
          fetchedTags = await fetchPaginatedInferenceData(
            startDateState,
            endDateState,
            modelName
          );
        }
        setInferenceData(fetchedTags ?? {});
        if (
          !fetchedTags ||
          !fetchedTags.tag_aliases ||
          fetchedTags.tag_aliases.length === 0
        ) {
          setIsPopupVisible(true);
          setInferenceData({});
        }
      } catch (e) {
        console.log(e);
        toast.error("Error fetching inference data for selected time range.");
        setInferenceData({});
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [startDateState, endDateState]);

  return (
    <>
      <div className="flex justify-center flex-col gap-10 items-center mt-4 mb-20">
        {loading && <Loading />}
        <FormCard>
          <div>
            <span className="font-semibold text-2xl text-btn-primary">
              Inference
            </span>
            <p className="mt-3 text-[14.5px]">
              Select a model to observe its generated inference comparison with
              the ground truth data in the selected time period.
            </p>
            <form action="" className="mt-8">
              {/* assembly, dgs tags */}
              <div className="flex gap-4 flex-col">
                <div className="flex gap-4">
                  <div className="flex flex-col w-full">
                    <label htmlFor="assembly" className="mb-1">
                      Assembly*
                    </label>
                    <Select
                      id="assembly"
                      className="w-full"
                      placeholder="Select Assembly"
                      options={(dropdownOptions.assemblyOptions ?? []).filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ""
                      )}
                      isClearable
                      isSearchable
                      required
                      styles={customDropdownStyles}
                      value={selectedValues.selectedAssembly}
                      onChange={(option) =>
                        handleSelectChange("selectedAssembly", option)
                      }
                    />
                    {errors.selectedAssembly && (
                      <p className="text-red-500 text-[14.5px] mt-1">
                        {errors.selectedAssembly}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full">
                    <label htmlFor="dgs-tags" className="mb-1">
                      Dry Gas Seal Tags*
                    </label>
                    <Select
                      id="dgs-tags"
                      placeholder="Select Dry Gas Seal Tags"
                      isClearable
                      isSearchable
                      required
                      styles={customDropdownStyles}
                      options={(
                        dropdownOptions.dryGasSealTagsOptions ?? []
                      ).filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ""
                      )}
                      value={selectedValues.selectedDryGasSealTags}
                      onChange={(option) =>
                        handleSelectChange("selectedDryGasSealTags", option)
                      }
                    />
                    {errors.selectedDryGasSealTags && (
                      <p className="text-red-500 text-[14.5px] mt-1">
                        {errors.selectedDryGasSealTags}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full">
                    <label htmlFor="dgs-tags" className="mb-1">
                      Tags
                    </label>
                    <Select
                      id="dgs-tags"
                      placeholder="Select the Tags"
                      isClearable
                      isSearchable
                      isMulti
                      styles={customDropdownStyles}
                      options={(dropdownOptions.tagsOptions ?? []).filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ""
                      )}
                      value={selectedValues.selectedTags}
                      onChange={(option) =>
                        handleSelectChange("selectedTags", option)
                      }
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col mt-2  w-full">
                    <label htmlFor="start-date" className="mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      className="custom-input"
                      value={startDateState}
                      onChange={(e) => setStartDateState(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col mt-2  w-full">
                    <label htmlFor="end-date" className="mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="end-date"
                      className="custom-input "
                      value={endDateState}
                      onChange={(e) => setEndDateState(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </FormCard>
        {activeModels.length !== 0 &&
          selectedValues.selectedAssembly &&
          selectedValues.selectedDryGasSealTags ? (
          <ActiveModelsTable data={activeModels} />
        ) : (
          ""
        )}
        {inferenceData &&
          inferenceData.tag_aliases &&
          inferenceData.tag_aliases.length !== 0 &&
          selectedValues.selectedDryGasSealTags ? (
          <InferenceTimeseriesChart
            inferenceData={inferenceData}
            selectedAnomaly={selectedAnomaly}
          />
        ) : (
          ""
        )}
        {inferenceData &&
          inferenceData.tag_aliases &&
          inferenceData.tag_aliases.length !== 0 &&
          selectedValues.selectedDryGasSealTags ? (
          <AnomaliesTable
            data={inferenceData}
            selectedTags={selectedValues.selectedDryGasSealTags}
            onRowSelect={setSelectedAnomaly}
          />
        ) : (
          ""
        )}
      </div>

      {isPopupVisible && (
        <ConfirmationPopup
          message={`No inference available for this time range.`}
          onConfirm={() => setIsPopupVisible(false)}
          onCancel={() => setIsPopupVisible(false)}
          cancelButtonMsg={""}
          confirmButtonMsg={"OK"}
        />
      )}
    </>
  );
};

export default InferenceForm;
